<template>
  <div class="start-journey">
    <div class="start-journey-wrap">
      <div class="start-journey-logo">
        <div class="start-journey-logo__kaia" />
        <CorporateHeader :corporate="corporate" />
      </div>

      <div class="start-journey-info">
        <h2
          class="start-journey-info__header"
          v-html="$t('b2b__landing_page__start_journey__title')"
        />
        <div
          class="start-journey-info__text"
          v-html="$t('b2b__landing_page__start_journey__back__description')"
        />
        <BaseButton
          ref="ctaButtonRef"
          @click="handleCTAButtonClick"
        >
          {{ ctaBtnTitle }}
        </BaseButton>
      </div>
    </div>
    <div :class="['start-journey-large-banner', env.isCopd ? 'copd' : '']" />
  </div>
</template>

<script setup>
import Corporate from '@shared/data/models/corporate.js'
import { onMounted, ref } from 'vue'
import env from '@shared/env.js'
import BaseButton from '@shared/components/BaseButton.vue'
import CorporateHeader from '@shared/components/CorporateHeader.vue'

const props = defineProps({
  ctaBtnTitle: {
    type: String,
    default: '',
  },
  corporate: {
    type: Corporate,
    default: null,
  },
  handleCTAButtonClick: {
    type: Function,
    default: () => {},
  },
  receiveCTAButtonClientRect: {
    type: Function,
    default: () => {},
  },
})

const ctaButtonRef = ref()

onMounted(() =>
  props.receiveCTAButtonClientRect(() =>
    ctaButtonRef.value.$el.getBoundingClientRect(),
  ),
)
</script>

<style lang="scss" scoped>
.start-journey {
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 $kds-space-3x 40px;
  background-color: transparent;
  position: relative;

  &-logo {
    position: relative;
    z-index: 1;
    background-color: $kds-color-background-base;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 65px;

    &__kaia {
      width: 100px;
      height: 15px;
      background-image: url('@shared/assets/img/kaia_text_logo.svg');
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
    }

    &__corporate {
      max-height: 50%;
    }
  }

  &__cta {
    @include kds-typography-title-medium;
    background-color: $kds-color-background-base-inverse;
    border: none;
    display: block;
    margin: $kds-space-3x 0;
    padding: $kds-space-2x $kds-space-3x 17px;
    width: 272px;
    color: $kds-color-text-primary-inverse;
    cursor: pointer;
    border-radius: 40px;

    &::after {
      content: 'w';
      display: inline-block;
      width: 24px;
      margin-left: 7px;
      background-image: url(@shared/assets/img/short_right_arrow.svg);
      background-position: left top;
      background-repeat: no-repeat;
      background-size: 26px 52px;
      color: transparent;
    }

    &:hover,
    &:focus {
      background-color: $kds-color-background-interactive-accent-hover;
    }

    &:active,
    &:visited {
      background-color: $kds-color-background-interactive-accent;
    }
  }

  &-info {
    &__header {
      @include kds-typography-display-small;
      margin: 0;
    }

    &__text {
      @include kds-typography-paragraph-large;
      margin: 13px 0 $kds-space-3x;
    }
  }

  &-large-banner {
    display: block;
    background-image: url('@shared/assets/img/thomas-copd-exercise-4172cr.jpeg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

    margin-top: $kds-space-3x;
    height: 327px;
    border-radius: 12px;

    &.copd {
      background-position: top;
      background-image: url('@shared/assets/img/landing-page-main-img-copd.jpg');
    }
  }
}

.start-journey {
  @include media-up(sm) {
    padding-bottom: $kds-space-4x;
  }

  @include media-up(md) {
    padding-right: 54px;
    padding-bottom: $kds-space-8x;
    padding-left: 54px;
  }

  @include media-up(lg) {
    display: flex;
    justify-content: space-between;
    padding: 60px 84px;
  }

  @include media-up(xxl) {
    padding-right: 120px;
    padding-left: 120px;
  }

  &-logo {
    @include media-up(md) {
      height: 77px;
    }

    @include media-up(lg) {
      padding: 0 0 35px;
      justify-content: flex-start;
    }

    @include media-up(xl) {
      padding-top: $kds-space-2_5x;
      padding-bottom: 40px;
    }

    @include media-up(xxl) {
      padding-left: 0;
    }

    &__kaia {
      @include media-up(md) {
        width: 180px;
        height: 26px;
      }

      @include media-up(lg) {
        width: 89px;
        height: 89px;
        background-image: url('@shared/assets/img/kaia_img_logo.svg');
        margin-right: 47px;
      }
    }

    &__corporate {
      @include media-up(lg) {
        max-height: 65%;
      }
    }
  }

  &-info {
    @include media-up(lg) {
      padding-right: 35px;
      padding-left: 0;
    }

    @include media-up(xl) {
      padding-right: $kds-space-2_5x;
    }

    @include media-up(xxl) {
      padding-left: 0;
      padding-right: 50px;
    }

    @include media-up(xxxl) {
      padding-right: 120px;
    }

    &__header {
      @include media-up(md) {
        @include kds-typography-display-medium;
        margin-top: 33px;
      }

      @include media-up(lg) {
        margin-top: 0;
        margin-bottom: 21px;
      }

      @include media-up(xl) {
        @include kds-typography-display-large;
        padding-right: 60px;
      }

      @include media-up(xxl) {
        padding-right: 100px;
      }
    }

    &__text {
      @include media-up(xl) {
        margin-bottom: 34px;
      }
    }
  }

  &-wrap {
    @include media-up(lg) {
      flex-basis: 46%;
    }
  }

  &-large-banner {
    @include media-up(md) {
      width: 100%;
      height: 680px;
    }

    @include media-up(lg) {
      flex-basis: 54%;
      border-radius: 24px;
      margin-top: 0;
    }
  }
}
</style>
